'use client';

import type {
  SurfaceProfileDto,
  UpsertSurfaceProfileDto,
} from '@terra/shared-contracts-external-network';
import { VStack } from '@terra/ui-styled-system';
import { useState } from 'react';
import {
  getTextFieldValue,
  type BusinessProfileTextField,
} from './business-profile.model';
import { BrandsServicesProductsEditor } from './BrandsServicesProductsEditor';
import { BusinessProfileCompanySizeDialog } from './BusinessProfileCompanySizeDialog';
import { BusinessProfileMetadataSection } from './BusinessProfileMetadataSection';
import { BusinessProfileTextFieldDialog } from './BusinessProfileTextFieldDialog';
import { DivisionsEditor } from './DivisionsEditor';
import { LocationsEditor } from './LocationsEditor';
import { SubsidiariesEditor } from './SubsidiariesEditor';

const COMPANY_SIZE_DIALOG = 'companySize';

type BusinessProfileEditDialog =
  | BusinessProfileTextField
  | typeof COMPANY_SIZE_DIALOG
  | null;

export type BusinessProfileContentProps = {
  profile: SurfaceProfileDto | null | undefined;
  isLoading: boolean;
  isError: boolean;
  isSaving: boolean;
  isReadOnly: boolean;
  onRetry: () => void;
  onSave: (dto: UpsertSurfaceProfileDto) => Promise<boolean>;
};

export function BusinessProfileContent({
  profile,
  isLoading,
  isError,
  isSaving,
  isReadOnly,
  onRetry,
  onSave,
}: BusinessProfileContentProps) {
  const [editDialog, setEditDialog] = useState<BusinessProfileEditDialog>(null);
  const textField =
    editDialog === COMPANY_SIZE_DIALOG || editDialog === null
      ? null
      : editDialog;
  const closeDialog = () => setEditDialog(null);

  return (
    <VStack alignItems="stretch" gap={8} w="full">
      <BusinessProfileMetadataSection
        profile={profile}
        isLoading={isLoading}
        isError={isError}
        isReadOnly={isReadOnly}
        isDisabled={isReadOnly || isSaving}
        onRetry={onRetry}
        onEditTextField={setEditDialog}
        onEditCompanySize={() => setEditDialog(COMPANY_SIZE_DIALOG)}
      />

      <LocationsEditor
        values={profile?.locations ?? []}
        isReadOnly={isReadOnly}
        isSaving={isSaving}
        onSave={(locations) => onSave({ locations })}
      />

      <BrandsServicesProductsEditor
        values={profile?.brandsServicesProducts ?? []}
        isReadOnly={isReadOnly}
        isSaving={isSaving}
        onSave={(brandsServicesProducts) => onSave({ brandsServicesProducts })}
      />

      <DivisionsEditor
        divisions={profile?.divisions ?? []}
        isReadOnly={isReadOnly}
        isSaving={isSaving}
        onSave={(divisions) => onSave({ divisions })}
      />

      <SubsidiariesEditor
        subsidiaries={profile?.subsidiaries ?? []}
        isReadOnly={isReadOnly}
        isSaving={isSaving}
        onSave={(subsidiaries) => onSave({ subsidiaries })}
      />

      <BusinessProfileTextFieldDialog
        field={textField}
        initialValue={
          textField === null
            ? ''
            : (getTextFieldValue({ profile, field: textField }) ?? '')
        }
        loading={isSaving}
        onClose={closeDialog}
        onSubmit={onSave}
      />

      <BusinessProfileCompanySizeDialog
        open={editDialog === COMPANY_SIZE_DIALOG}
        initialValue={profile?.companySize ?? null}
        loading={isSaving}
        onClose={closeDialog}
        onSubmit={onSave}
      />
    </VStack>
  );
}
